import { useNavigate } from "react-router-dom";
import { FC, memo, useRef, useState, useCallback, useMemo } from "react";
import cn from "classnames";
import {
  Button,
  Container,
  FileItem,
  FileUpload,
  DropAcceptedFunc,
  useWillUnmount,
  useDidUpdate,
  notification,
  Nullable,
  useFileQueue,
} from "@epcnetwork/core-ui-kit";

import {
  getPipelineRoute,
  getAcceptedFilesText,
  getSupportedFormats,
  onFileRedirect,
  prepareSuppressValue,
} from "utils";
import { useTracking } from "hooks";
import { SMARTLOOK_EVENTS } from "constants/smartlook.constants";
import { PIPELINES_LIST_PAGE } from "constants/routes.constants";
import { postDeduplicateFile } from "api";
import { PipelineHeaderSection } from "../components/pipeline-header-section/pipeline-header-section";
import { FilePresetSwitch } from "../components/file-preset-switch/file-preset-switch";
import { DeduplicateFormModal } from "./form-modal/deduplicate-form-modal";
import {
  DeduplicateFileInfo,
  DeduplicateFileItem,
  PipelineDeduplicateData,
} from "./deduplicate.types";
import { supportedFormats, getInitialData } from "./deduplicate.constants";

import styles from "./deduplicate.module.css";

const DeduplicatePage: FC = memo(() => {
  const componentIsMounted = useRef(true);
  const navigate = useNavigate();

  const { pipelineSubmitEvent } = useTracking();

  const pipeline = getPipelineRoute("Deduplicate");

  const {
    files,
    createInitialFile,
    addFiles,
    updateFiles,
    removeFiles,
    clearEntity,
    getItem,
    isEntityInConfiguration,
    isEntityFinished,
    hasAllConfigured,
    submitOneByOne,
    hasAllSuccessFiles,
  } = useFileQueue<DeduplicateFileItem, PipelineDeduplicateData>(
    pipeline.title,
    postDeduplicateFile,
  );
  const presetFile: DeduplicateFileItem | undefined = useMemo(() => files[0], [files]);

  useWillUnmount(() => {
    componentIsMounted.current = false;
  });

  useDidUpdate(() => {
    if (!isEntityFinished) return;

    handleRedirect();
  }, [isEntityFinished]);

  const [editedFile, setEditedFile] = useState<Nullable<DeduplicateFileItem>>(null);
  const [isAppliedToAll, setIsAppliedToAll] = useState(false);

  const handleAcceptedDrop: DropAcceptedFunc = useCallback(
    (acceptedFiles) => {
      addFiles(
        acceptedFiles.map((file) =>
          createInitialFile(file, {
            isTuned: isAppliedToAll && presetFile?.isTuned,
            data:
              isAppliedToAll && presetFile
                ? {
                    ...presetFile.data,
                    fileName: file.name,
                  }
                : getInitialData(file),
          }),
        ),
      );
    },
    [addFiles, createInitialFile, isAppliedToAll, presetFile],
  );

  const handleClearClick = () => {
    notification.confirm(
      "Delete files",
      `Are you sure you want to delete all ${files.length} file(s)?`,
      {
        onOk: clearEntity,
        icon: "delete",
      },
    );
  };

  const handleConfigureItem = useCallback(
    (id: string) => {
      const item = getItem(id);
      if (!item) return;
      setEditedFile(item);
    },
    [getItem],
  );

  const handleModalSubmit = useCallback(
    ({ id, ...rest }: DeduplicateFileItem) => {
      updateFiles({ id, file: rest });
      setEditedFile(null);
    },
    [updateFiles],
  );

  const handleCloseModal = useCallback(() => setEditedFile(null), []);

  const handleSubmit = () => {
    const data = files.map((file) => {
      const formData = new FormData();

      const { fileName, hasHeaders, suppress, compareBasedOn, emailIndex } = file.data;
      const deduplicateData: DeduplicateFileInfo = {
        emailIndex,
        hasHeaders,
        compareEmails: compareBasedOn === "emails",
        suppress: prepareSuppressValue(emailIndex, suppress),
      };

      formData.append("fileInfo", JSON.stringify(deduplicateData));
      formData.append("file", file.originalFile, fileName);

      return {
        id: file.id,
        data: formData,
      };
    });

    submitOneByOne(data);
    pipelineSubmitEvent(SMARTLOOK_EVENTS.deduplicateSubmit, files);
  };

  const handleRedirect = () => {
    onFileRedirect({ files, clearEntity, navigate });
  };

  const handleRedirectToPipelines = () => {
    navigate(PIPELINES_LIST_PAGE.path);
  };

  return (
    <Container>
      <div className={styles.container}>
        <PipelineHeaderSection pipeline={pipeline} />

        <div className={styles.form}>
          <FileUpload
            className={cn(styles.dropZone, { [styles.uploadZone]: files.length })}
            uploadedFilesLength={files.length}
            subtitle={getAcceptedFilesText(getSupportedFormats(supportedFormats))}
            accept={supportedFormats}
            onDropAccepted={handleAcceptedDrop}
            exceedFilesOption="splice-with-error"
            disabled={!isEntityInConfiguration}
            preventDropOnDocument
            multiple
          />
          {files.length > 1 && (
            <FilePresetSwitch
              isAppliedToAll={isAppliedToAll}
              presetFile={presetFile}
              files={files}
              updateFiles={updateFiles}
              setIsAppliedToAll={setIsAppliedToAll}
            />
          )}
          <div className={styles.fileList}>
            {files.map(({ id, originalFile, data, ...rest }) => (
              <FileItem
                {...rest}
                key={id}
                id={id}
                file={originalFile}
                onCrossClick={removeFiles}
                onSetValuesClick={handleConfigureItem}
                onEditValuesClick={handleConfigureItem}
              >
                {data.headers.includes("email") && (
                  <div className={styles.additionalInfo}>
                    <span>Selected column:</span> email
                  </div>
                )}
              </FileItem>
            ))}
          </div>
          {editedFile && (
            <DeduplicateFormModal
              file={editedFile}
              setClose={handleCloseModal}
              onSubmitClick={handleModalSubmit}
            />
          )}
          <div className={styles.buttons}>
            <Button
              appearance="secondary"
              onClick={!files.length ? handleRedirectToPipelines : handleClearClick}
              disabled={isEntityFinished && !hasAllSuccessFiles}
            >
              Cancel
            </Button>
            {!isEntityFinished && (
              <Button
                onClick={handleSubmit}
                disabled={!hasAllConfigured || !files.length}
                loading={!isEntityInConfiguration}
              >
                Submit
              </Button>
            )}
          </div>
        </div>
      </div>
    </Container>
  );
});

export { DeduplicatePage };
